import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, IconButton, Typography, TextField, CircularProgress, Tooltip, Button } from '@mui/material';
import { styled, createTheme, ThemeProvider, useTheme, alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import { Loader2 } from 'lucide-react';

// Error Boundary Component
class ErrorBoundary extends React.Component {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return (
                <Box sx={{ p: 1, textAlign: 'center' }}>
                    <Typography color="error" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                        Something went wrong. Please try again or contact <a href="mailto:info@aiempower.ca" style={{ color: '#a855f7' }}>info@aiempower.ca</a>.
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ReplayIcon />}
                        onClick={() => this.setState({ hasError: false })}
                        sx={{ mt: 0.5, fontSize: '0.75rem', minWidth: '60px', backgroundColor: '#a855f7', textTransform: 'none', '&:focus': { outline: 'none', backgroundColor: '#9333ea' } }}
                        aria-label="Retry component"
                    >
                        Retry
                    </Button>
                </Box>
            );
        }
        return this.props.children;
    }
}

// RichMessage Component
const RichMessage = ({ markdown }) => {
    const theme = useTheme();

    return (
        <div>
            <ReactMarkdown
                children={markdown || ''}
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ node, ...props }) => (
                        <p style={{ margin: '8px 0', color: 'inherit' }} {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                        <strong style={{ color: '#a855f7' }} {...props} />
                    ),
                    code: ({ node, inline, children, ...props }) =>
                        inline ? (
                            <code
                                style={{
                                    background: 'rgba(168, 85, 247, 0.2)',
                                    padding: '2px 4px',
                                    borderRadius: '4px',
                                    color: 'inherit',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {children}
                            </code>
                        ) : (
                            <pre
                                style={{
                                    background: 'rgba(46, 26, 71, 0.8)',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    overflowX: 'auto',
                                    color: 'inherit',
                                    whiteSpace: 'pre-wrap',
                                    maxWidth: '100%',
                                }}
                            >
                                <code {...props}>{children}</code>
                            </pre>
                        ),
                    a: ({ node, ...props }) => (
                        <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: theme.palette?.primary?.main || '#2563eb',
                                textDecoration: 'underline',
                                fontWeight: 500,
                                transition: 'color 0.15s',
                                cursor: 'pointer',
                            }}
                            onMouseOver={e => (e.currentTarget.style.color = '#1e40af')}
                            onMouseOut={e => (e.currentTarget.style.color = theme.palette?.primary?.main || '#2563eb')}
                        >
                            {props.children}
                        </a>
                    ),
                }}
            />
        </div>
    );
};

// Theme definitions
const themes = {
    'blue-white': {
        primaryColor: '#3b82f6',
        secondaryColor: '#ffffff',
        background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
        textColor: '#000000',
        userMessageBg: 'rgba(59, 130, 246, 0.8)',
        assistantMessageBg: 'rgba(245, 245, 245, 0.9)',
        mode: 'light',
    },
    'healthcare': {
        primaryColor: '#10b981',
        secondaryColor: '#059669',
        background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
        textColor: '#1e293b',
        userMessageBg: 'rgba(16, 185, 129, 0.8)',
        assistantMessageBg: 'rgba(245, 245, 245, 0.9)',
        mode: 'light',
    },
    'Vuxo Theme': {
        primaryColor: '#a855f7',
        secondaryColor: '#3b82f6',
        background: 'linear-gradient(135deg, #1a0b2e, #2e1a47)',
        textColor: '#d1d5db',
        userMessageBg: 'rgba(59, 130, 246, 0.2)',
        assistantMessageBg: 'rgba(168, 85, 247, 0.2)',
        mode: 'dark',
    },
};

// Default theme
const defaultTheme = themes['Vuxo Theme'];

// Styled Components
const ChatToggleWrapper = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: 20,
    right: 20,
    cursor: 'pointer',
    zIndex: 1002,
    boxSizing: 'border-box',
    '& img': {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        loading: 'lazy',
    },
    touchAction: 'none',
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
}));

const ChatPanel = styled(Box)(({ theme, isFullScreen, width, height }) => ({
    position: isFullScreen ? 'fixed' : 'fixed',
    top: isFullScreen ? 0 : 'auto',
    left: isFullScreen ? 0 : 'auto',
    bottom: isFullScreen ? 0 : '60px',
    right: isFullScreen ? 0 : 20,
    width: isFullScreen ? '100%' : `${width}px`,
    height: isFullScreen ? '100%' : `${height}px`,
    minWidth: isFullScreen ? '100%' : '300px',
    minHeight: isFullScreen ? '100%' : '400px',
    maxWidth: isFullScreen ? '100%' : '90vw',
    maxHeight: isFullScreen ? '100%' : '80vh',
    background: theme.custom.background,
    borderRadius: isFullScreen ? '0' : '10px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1002,
    boxSizing: 'border-box',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
        minWidth: isFullScreen ? '100%' : '280px',
        minHeight: isFullScreen ? '100%' : '350px',
        right: isFullScreen ? 0 : '10px',
        bottom: isFullScreen ? 0 : '0',
    },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '12px',
    height: '12px',
    background: theme.palette.primary.main,
    clipPath: 'polygon(0 100%, 100% 100%, 0 0)',
    cursor: 'nwse-resize',
    zIndex: 1003,
    touchAction: 'none',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
}));

const Header = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '8px 8px 0 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    minHeight: '60px',
    flexShrink: 0,
}));

const MessageArea = styled(Box)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(1.5),
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    overscrollBehavior: 'contain',
    touchAction: 'pan-y',
    '&::-webkit-scrollbar': {
        width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.primary.main,
        borderRadius: '4px',
        boxShadow: '0 0 2px rgba(0,0,0,0.3)',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.primary.main} transparent`,
}));

const InputSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    background: 'rgba(255, 255, 255, 0.05)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    boxSizing: 'border-box',
}));

const Footer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    textAlign: 'center',
    fontSize: '10px',
    color: theme => alpha(theme.custom.textColor, 0.6),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
}));

const MessageBubble = styled(Box)(({ position }) => ({
    display: 'flex',
    justifyContent: position === 'right' ? 'flex-end' : 'flex-start',
    margin: '8px 0',
    width: '100%',
    boxSizing: 'border-box',
}));

const BubbleContent = styled(Box)(({ position, theme }) => ({
    background: position === 'right' ? theme.custom.userMessageBg : theme.custom.assistantMessageBg,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.25),
    borderRadius: '12px',
    maxWidth: '80%',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    boxSizing: 'border-box',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    [theme.breakpoints.up('md')]: {
        maxWidth: '600px',
    },
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
    flexGrow: 1,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
        background: alpha(theme.palette.background.paper, 0.2),
        color: theme.palette.text.primary,
        borderRadius: '20px',
        '& fieldset': {
            borderColor: 'rgba(168, 85, 247, 0.5)',
        },
        '&:hover fieldset': {
            borderColor: '#a855f7',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#a855f7',
            boxShadow: 'none',
        },
        fontSize: '1rem',
    },
    '& .MuiInputBase-input': {
        padding: theme.spacing(1.5),
        boxSizing: 'border-box',
    },
    [theme.breakpoints.down('sm')]: {
        '& .MuiInputBase-input': {
            fontSize: '0.95rem',
            padding: theme.spacing(1.2),
        },
    },
}));

const CustomIconButton = styled(IconButton)(({ theme }) => ({
    padding: theme.spacing(1),
    width: '44px',
    height: '44px',
    transition: 'all 0.3s ease',
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5),
    },
}));

const ErrorAlert = styled(motion(Box))(({ theme }) => ({
    background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
    color: '#ffffff',
    padding: theme.spacing(1),
    borderRadius: '12px',
    margin: theme.spacing(1),
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    boxSizing: 'border-box',
    minHeight: '80px',
}));

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    '& .MuiTooltip-tooltip': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: '0.8rem',
        padding: '8px 14px',
        borderRadius: '8px',
        boxShadow: '0 0 8px rgba(168, 85, 247, 0.4)',
        fontFamily: '"Poppins", sans-serif',
        transformOrigin: 'center',
    },
    '& .MuiTooltip-arrow': {
        color: theme.palette.primary.main,
    },
}));

const AnimatedTooltip = ({ title, children, ...props }) => (
    <CustomTooltip title={title} arrow placement="top" enterDelay={500} {...props}>
        {children}
    </CustomTooltip>
);

const ShinyLink = styled('a')(({ theme, isFooter }) => ({
    color: theme.palette.text.primary,
    background: `linear-gradient(to right, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.main} 100%)`,
    backgroundSize: '200%',
    backgroundPosition: '0%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'shine 5s infinite cubic-bezier(0.4, 0, 0.6, 1)',
    fontWeight: 600,
    fontSize: isFooter ? '10px' : '12px',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    fontFamily: '"Poppins", sans-serif',
    padding: isFooter ? '2px 6px' : '4px 8px',
    fontVariant: isFooter ? 'small-caps' : 'normal',
    willChange: 'background-position',
    marginLeft: '-8px',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(0.98)',
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    '@keyframes shine': {
        '0%': { backgroundPosition: '0%' },
        '100%': { backgroundPosition: '200%' },
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: isFooter ? '8px' : '10px',
        padding: isFooter ? '2px 4px' : '3px 6px',
        marginLeft: '-6px',
    },
}));

// Suggested Questions Component
const SuggestedQuestions = ({ questions, onQuestionClick, error }) => {
    const theme = useTheme();
    const controls = useAnimationControls();
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        }
    }, [questions]);

    useEffect(() => {
        if (questions.length > 0) {
            const totalWidth = questions.length * 180;
            controls.start({
                x: [0, -totalWidth],
                transition: {
                    x: {
                        repeat: Infinity,
                        repeatType: 'loop',
                        duration: questions.length * 5,
                        ease: 'linear',
                    },
                },
            });
        }
    }, [questions, controls]);

    const handleKeyDown = (e, question) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onQuestionClick(question);
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                height: 'auto',
                maxHeight: '60px',
                overflow: 'hidden',
                padding: theme.spacing(1),
                background: 'rgba(255, 255, 255, 0.05)',
                borderTop: `1px solid ${theme => alpha(theme.palette.primary.main, 0.3)}`,
                borderBottom: `1px solid ${theme => alpha(theme.palette.primary.main, 0.3)}`,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
            }}
            ref={containerRef}
        >
            {error ? (
                <Typography sx={{ color: '#ef4444', fontStyle: 'italic', textAlign: 'center', width: '100%', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                    {error}
                </Typography>
            ) : questions.length === 0 ? (
                <Typography sx={{ color: theme.palette.text.primary, fontStyle: 'italic', textAlign: 'center', width: '100%', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                    Generating suggested questions...
                </Typography>
            ) : (
                <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                    <motion.div
                        animate={controls}
                        style={{ display: 'flex', flexDirection: 'row' }}
                    >
                        {[...questions, ...questions].map((q, index) => (
                            <Box
                                key={`${index}-${q}`}
                                onClick={() => onQuestionClick(q)}
                                onKeyDown={(e) => handleKeyDown(e, q)}
                                tabIndex={0}
                                role="button"
                                aria-label={`Send question: ${q}`}
                                sx={{
                                    background: theme => alpha(theme.palette.primary.main, 0.4),
                                    color: theme.palette.text.primary,
                                    borderRadius: '20px',
                                    padding: theme.spacing(1, 2),
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    border: `1px solid ${theme => alpha(theme.palette.primary.main, 0.6)}`,
                                    transition: 'all 0.3s ease',
                                    whiteSpace: 'nowrap',
                                    marginRight: theme.spacing(1.5),
                                    minHeight: '36px',
                                    '&:hover': {
                                        background: theme => alpha(theme.palette.primary.main, 0.6),
                                        transform: 'scale(1.02)',
                                        boxShadow: `0 2px 8px ${theme => alpha(theme.palette.primary.main, 0.3)}`,
                                    },
                                    '&:focus': {
                                        outline: 'none',
                                        backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                                    },
                                }}
                            >
                                {q}
                            </Box>
                        ))}
                    </motion.div>
                </Box>
            )}
        </Box>
    );
};

// Main Chat Widget Component
const EmbeddableChat = () => {
    const { identifier } = useParams();
    const [searchParams] = useSearchParams();

    // Parse query parameters
    const isInitiallyOpen = searchParams.get('isInitiallyOpen') !== 'false';
    const initialWidth = Math.max(300, Math.min(800, parseInt(searchParams.get('width'), 10) || 350));
    const initialHeight = Math.max(400, Math.min(800, parseInt(searchParams.get('height'), 10) || 500));
    const isFullScreenInitial = searchParams.get('isFullScreen') === 'true';
    const isAlways = searchParams.get('isAlways') === 'true';

    // State
    const [isOpen, setIsOpen] = useState(isInitiallyOpen);
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [botDetails, setBotDetails] = useState(null);
    const [error, setError] = useState(null);
    const [messageCount, setMessageCount] = useState(0);
    const [isSending, setIsSending] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [suggestedQuestions, setSuggestedQuestions] = useState([]);
    const [questionError, setQuestionError] = useState(null);
    const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
    const [isBotValid, setIsBotValid] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(isAlways ? true : isFullScreenInitial);
    const [width, setWidth] = useState(() => {
        const saved = localStorage.getItem(`chatWidth_${identifier}`);
        return isAlways ? window.innerWidth : (saved ? Math.max(300, Math.min(800, parseInt(saved, 10))) : initialWidth);
    });
    const [height, setHeight] = useState(() => {
        const saved = localStorage.getItem(`chatHeight_${identifier}`);
        return isAlways ? window.innerHeight : (saved ? Math.max(400, Math.min(800, parseInt(saved, 10))) : initialHeight);
    });
    const [retryCount, setRetryCount] = useState(0);

    // Refs
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const toggleRef = useRef(null);
    const questionRetryCountRef = useRef(0);
    const resizeRef = useRef(null);
    const isResizing = useRef(false);
    const startX = useRef(0);
    const startY = useRef(0);
    const startWidth = useRef(0);
    const startHeight = useRef(0);

    // Environment variables
    const getPublicInfoUrl = import.meta.env.VITE_GET_PUBLIC_BOT_INFO_URL;
    const chatUrl = import.meta.env.VITE_CHAT_URL;
    const generateQuestionsUrl = import.meta.env.VITE_GENERATE_QUESTIONS_URL;

    // Persist size to localStorage (skip if isAlways)
    useEffect(() => {
        if (!isAlways) {
            localStorage.setItem(`chatWidth_${identifier}`, width);
            localStorage.setItem(`chatHeight_${identifier}`, height);
        }
    }, [width, height, identifier, isAlways]);

    // Auto-focus input when chat opens
    useEffect(() => {
        if (isOpen && !isFullScreen) {
            setTimeout(() => inputRef.current?.focus(), 400);
        }
    }, [isOpen, isFullScreen]);

    // Resize handler
    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        isResizing.current = true;
        startX.current = e.clientX || (e.touches && e.touches[0].clientX);
        startY.current = e.clientY || (e.touches && e.touches[0].clientY);
        startWidth.current = width;
        startHeight.current = height;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'nwse-resize';
    }, [width, height]);

    const handleResizeMove = useCallback((e) => {
        if (!isResizing.current) return;
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        if (clientX && clientY) {
            const deltaX = clientX - startX.current;
            const deltaY = clientY - startY.current;
            const newWidth = Math.max(300, Math.min(800, startWidth.current + deltaX));
            const newHeight = Math.max(400, Math.min(800, startHeight.current + deltaY));
            requestAnimationFrame(() => {
                setWidth(newWidth);
                setHeight(newHeight);
            });
        }
    }, []);

    const handleResizeEnd = useCallback(() => {
        if (isResizing.current) {
            isResizing.current = false;
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        }
    }, []);

    useEffect(() => {
        const moveHandler = (e) => {
            e.preventDefault();
            handleResizeMove(e);
        };
        const endHandler = () => handleResizeEnd();
        if (isResizing.current) {
            document.addEventListener('mousemove', moveHandler);
            document.addEventListener('mouseup', endHandler);
            document.addEventListener('touchmove', moveHandler, { passive: false });
            document.addEventListener('touchend', endHandler);
        }
        return () => {
            document.removeEventListener('mousemove', moveHandler);
            document.removeEventListener('mouseup', endHandler);
            document.removeEventListener('touchmove', moveHandler);
            document.removeEventListener('touchend', endHandler);
        };
    }, [handleResizeMove, handleResizeEnd]);

    // Audio objects
    const openSound = useMemo(() => {
        const audio = new Audio('https://storage.googleapis.com/aiempower-public-media/Sounds/OpenChat.wav');
        audio.preload = 'metadata';
        return audio;
    }, []);
    const closeSound = useMemo(() => {
        const audio = new Audio('https://storage.googleapis.com/aiempower-public-media/Sounds/Close.wav');
        audio.preload = 'metadata';
        return audio;
    }, []);
    const receiveSound = useMemo(() => {
        const audio = new Audio('https://storage.googleapis.com/aiempower-public-media/Sounds/Receive.wav');
        audio.preload = 'metadata';
        return audio;
    }, []);

    // Default bot setup
    const defaultBotDetails = {
        botName: 'Assistant',
        botImageUrl: 'https://i.imgur.com/2OPgGx8.gif',
        welcomeMessage: 'Hello! I’m here to help. Ask me anything.',
        themeId: 'Vuxo Theme',
        botId: null,
    };

    // Auto-scroll to bottom
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Reset message count daily
    useEffect(() => {
        const storedData = localStorage.getItem(`messageCount_${identifier}`);
        if (storedData) {
            const { count, date } = JSON.parse(storedData);
            const storedDate = new Date(date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            storedDate.setHours(0, 0, 0, 0);
            if (storedDate < today) {
                setMessageCount(0);
                localStorage.setItem(`messageCount_${identifier}`, JSON.stringify({ count: 0, date: new Date().toISOString() }));
            } else {
                setMessageCount(parseInt(count, 10));
            }
        }
    }, [identifier]);

    // Fetch bot info
    const fetchBotInfo = useCallback(async () => {
        try {
            if (!identifier) {
                throw new Error('No bot identifier provided in URL');
            }
            if (!getPublicInfoUrl) {
                throw new Error('Bot info URL not configured');
            }
            const response = await fetch(`${getPublicInfoUrl}?shareId=${identifier}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch bot info: ${response.status}`);
            }
            const data = await response.json();
            if (data.success && data.publicBotInfo) {
                setBotDetails(data.publicBotInfo);
                setMessages([{
                    position: 'left',
                    text: data.publicBotInfo.welcomeMessage || `Hello! I'm ${data.publicBotInfo.botName}. How can I help?`,
                    id: 'welcome',
                    date: new Date(),
                }]);
                setSuggestedQuestions(data.publicBotInfo.generatedQuestions || []);
                setIsBotValid(true);
            } else {
                throw new Error('Invalid bot data');
            }
        } catch (err) {
            console.error('[EmbeddableChat] Bot fetch error:', err);
            setError(`Failed to load bot details: ${err.message}. Please try again or contact info@aiempower.ca.`);
            setBotDetails(defaultBotDetails);
            setMessages([{
                position: 'left',
                text: defaultBotDetails.welcomeMessage,
                id: 'welcome',
                date: new Date(),
            }]);
            setIsBotValid(false);
        } finally {
            setIsLoading(false);
        }
    }, [identifier, getPublicInfoUrl]);

    useEffect(() => {
        if (identifier) {
            fetchBotInfo();
        } else {
            setError('No bot identifier provided. Please try again or contact info@aiempower.ca.');
            setBotDetails(defaultBotDetails);
            setMessages([{
                position: 'left',
                text: defaultBotDetails.welcomeMessage,
                id: 'welcome',
                date: new Date(),
            }]);
            setIsBotValid(false);
            setIsLoading(false);
        }
    }, [identifier, fetchBotInfo]);

    // Generate suggested questions
    const generateQuestions = useCallback(async (currentBotId) => {
        if (!currentBotId || !generateQuestionsUrl || isGeneratingQuestions || questionRetryCountRef.current >= 3) {
            return;
        }
        setIsGeneratingQuestions(true);
        setQuestionError(null);
        try {
            const response = await fetch(generateQuestionsUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ botId: currentBotId }),
            });
            if (!response.ok) {
                throw new Error(`Failed to generate questions: ${response.status}`);
            }
            const result = await response.json();
            setSuggestedQuestions(result.questions || []);
            questionRetryCountRef.current = 0;
        } catch (err) {
            console.error('[EmbeddableChat] Question generation error:', err);
            questionRetryCountRef.current += 1;
            setQuestionError(`Failed to load suggested questions (Attempt ${questionRetryCountRef.current}/3). Please try again or contact info@aiempower.ca.`);
            if (questionRetryCountRef.current >= 3) {
                setQuestionError('Maximum retries reached for suggested questions. Please contact info@aiempower.ca.');
            }
        } finally {
            setIsGeneratingQuestions(false);
        }
    }, [generateQuestionsUrl, isGeneratingQuestions]);

    useEffect(() => {
        if (isBotValid && botDetails?.botId && !suggestedQuestions.length && !isGeneratingQuestions) {
            generateQuestions(botDetails.botId);
        }
    }, [isBotValid, botDetails, generateQuestions, isGeneratingQuestions]);

    // Theme setup
    const selectedTheme = botDetails && themes[botDetails.themeId] ? themes[botDetails.themeId] : defaultTheme;
    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: selectedTheme.mode || 'light',
            primary: { main: selectedTheme.primaryColor },
            secondary: { main: selectedTheme.secondaryColor },
            text: { primary: selectedTheme.textColor },
        },
        custom: {
            background: selectedTheme.background,
            userMessageBg: selectedTheme.userMessageBg,
            assistantMessageBg: selectedTheme.assistantMessageBg,
            textColor: selectedTheme.textColor,
        },
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 900,
                lg: 1200,
                xl: 1536,
            },
        },
    }), [selectedTheme]);

    // Handle toggle
    const handleToggle = useCallback(() => {
        if (isOpen) {
            closeSound.play().catch(() => { });
            setIsOpen(false);
            toggleRef.current?.focus();
        } else {
            setIsOpen(true);
        }
    }, [isOpen, closeSound]);

    const handleToggleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
        }
    };

    // Handle full-screen toggle
    const handleFullScreenToggle = () => {
        setIsFullScreen(prev => !prev);
        if (!isFullScreen) {
            inputRef.current?.focus();
        }
    };

    const handleFullScreenKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleFullScreenToggle();
        }
    };

    // Handle send
    const handleSend = useCallback(async () => {
        if (!botDetails?.botId) {
            setError('Cannot send message: bot is not properly configured. Please try again or contact info@aiempower.ca.');
            return;
        }
        if (messageCount >= 15) {
            setError('Message limit reached (15 messages). Please try again tomorrow or contact info@aiempower.ca.');
            return;
        }
        if (!userInput.trim() || isSending) return;
        setIsSending(true);
        setIsTyping(true);
        const newMessage = { position: 'right', text: userInput, id: Date.now(), date: new Date() };
        setMessages(prev => [...prev, newMessage]);
        setUserInput('');

        try {
            const minTypingDuration = 1000;
            const history = messages.filter(m => m.id !== 'welcome').map(m => ({
                role: m.position === 'right' ? 'user' : 'assistant',
                content: m.text,
            }));
            const fetchPromise = fetch(chatUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ botConfigId: botDetails?.botId || identifier, message: userInput, history }),
            });
            const delayPromise = new Promise(resolve => setTimeout(resolve, minTypingDuration));
            const [response] = await Promise.all([fetchPromise, delayPromise]);

            if (!response.ok) throw new Error('Failed to send message');
            const data = await response.json();
            const assistantMessage = {
                position: 'left',
                text: data.reply || 'Sorry, something went wrong.',
                id: Date.now() + 1,
                date: new Date(),
            };
            setMessages(prev => [...prev, assistantMessage]);
            receiveSound.play().catch(() => { });
            const newCount = messageCount + 1;
            setMessageCount(newCount);
            localStorage.setItem(`messageCount_${identifier}`, JSON.stringify({ count: newCount, date: new Date().toISOString() }));
        } catch (err) {
            setError('Failed to send message. Please try again or contact info@aiempower.ca.');
        } finally {
            setIsSending(false);
            setIsTyping(false);
        }
    }, [messageCount, userInput, isSending, messages, botDetails, identifier, chatUrl, receiveSound]);

    const handleSendKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Handle question click
    const handleQuestionClick = useCallback((question) => {
        if (!botDetails?.botId) {
            setError('Cannot send message: bot is not properly configured. Please try again or contact info@aiempower.ca.');
            return;
        }
        if (messageCount >= 15) {
            setError('Message limit reached (15 messages). Please try again tomorrow or contact info@aiempower.ca.');
            return;
        }
        setUserInput(question);
        handleSend();
    }, [botDetails, messageCount, handleSend]);

    const handleLinkKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            window.open('https://vuxo.aiempower.ca', '_blank', 'noopener,noreferrer');
        }
    };

    const handleRetry = () => {
        if (retryCount >= 3) {
            setError('Maximum retries reached. Please contact info@aiempower.ca.');
            return;
        }
        setError(null);
        setQuestionError(null);
        setIsLoading(true);
        questionRetryCountRef.current = 0;
        setRetryCount(prev => prev + 1);
        fetchBotInfo();
    };

    const handleRefresh = () => {
        window.location.reload();
    };

    // Dynamic input placeholder
    const inputPlaceholder = !botDetails?.botId
        ? "Bot is not configured"
        : messageCount >= 15
            ? "Message limit reached"
            : "Type a message...";

    return (
        <ThemeProvider theme={muiTheme}>
            {!isOpen && (
                <AnimatedTooltip title="Open chat">
                    <ChatToggleWrapper
                        ref={toggleRef}
                        onClick={handleToggle}
                        onKeyDown={handleToggleKeyDown}
                        tabIndex={0}
                        role="button"
                        aria-label="Open chat"
                    >
                        <img src="https://i.imgur.com/U2Dz59P.png" alt="Chat" loading="lazy" />
                    </ChatToggleWrapper>
                </AnimatedTooltip>
            )}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, translateX: '100%' }}
                        animate={{ opacity: 1, translateX: 0 }}
                        exit={{ opacity: 0, translateX: '100%' }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        style={{ overflow: 'hidden' }}
                        onTouchStart={(e) => e.preventDefault()}
                    >
                        <ChatPanel isFullScreen={isFullScreen} width={width} height={height}>
                            <Header>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <AnimatedTooltip title="Visit VuxoAI website">
                                        <a
                                            href="https://vuxo.aiempower.ca"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            tabIndex={0}
                                            aria-label="Visit VuxoAI website"
                                            onKeyDown={handleLinkKeyDown}
                                        >
                                            <img src="https://i.imgur.com/MtQvDiz.png" alt="Vuxo AI" style={{ height: '30px' }} loading="lazy" />
                                        </a>
                                    </AnimatedTooltip>
                                    <AnimatedTooltip title="Powered By VuxoAI">
                                        <ShinyLink
                                            href="https://vuxo.aiempower.ca"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            tabIndex={0}
                                            aria-label="Powered By VuxoAI"
                                            onKeyDown={handleLinkKeyDown}
                                        >
                                            VuxoAI
                                        </ShinyLink>
                                    </AnimatedTooltip>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ color: 'text.primary', fontSize: { xs: '1rem', sm: '1.25rem' } }}
                                    >
                                        {botDetails?.botName || 'Assistant'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {!isAlways && (
                                        <>
                                            <AnimatedTooltip title={isFullScreen ? "Exit full screen" : "Enter full screen"}>
                                                <CustomIconButton
                                                    onClick={handleFullScreenToggle}
                                                    onKeyDown={handleFullScreenKeyDown}
                                                    color="primary"
                                                    tabIndex={0}
                                                    aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
                                                >
                                                    {isFullScreen ? (
                                                        <FullscreenExitIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                                                    ) : (
                                                        <FullscreenIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                                                    )}
                                                </CustomIconButton>
                                            </AnimatedTooltip>
                                            <AnimatedTooltip title="Close chat">
                                                <CustomIconButton
                                                    onClick={handleToggle}
                                                    onKeyDown={handleToggleKeyDown}
                                                    color="primary"
                                                    tabIndex={0}
                                                    aria-label="Close chat"
                                                >
                                                    <CloseIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                                                </CustomIconButton>
                                            </AnimatedTooltip>
                                        </>
                                    )}
                                </Box>
                            </Header>
                            <MessageArea aria-live="polite">
                                {isLoading ? (
                                    <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 2 }} />
                                ) : (
                                    <>
                                        <AnimatePresence>
                                            {error && (
                                                <ErrorAlert
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    transition={{ duration: 0.3 }}
                                                    role="alert"
                                                    aria-describedby="error-message"
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        <Typography id="error-message" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                                                            {error}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<ReplayIcon />}
                                                            onClick={handleRetry}
                                                            sx={{ fontSize: '0.75rem', minWidth: '60px', backgroundColor: '#a855f7', textTransform: 'none', '&:focus': { outline: 'none', backgroundColor: '#9333ea' } }}
                                                            aria-label="Retry operation"
                                                        >
                                                            Retry
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<RefreshIcon />}
                                                            onClick={handleRefresh}
                                                            sx={{ fontSize: '0.75rem', minWidth: '60px', backgroundColor: '#a855f7', textTransform: 'none', '&:focus': { outline: 'none', backgroundColor: '#9333ea' } }}
                                                            aria-label="Refresh page"
                                                        >
                                                            Refresh
                                                        </Button>
                                                    </Box>
                                                </ErrorAlert>
                                            )}
                                        </AnimatePresence>
                                        {messages.map(msg => (
                                            <MessageBubble key={msg.id} position={msg.position}>
                                                {msg.position === 'left' && botDetails?.botImageUrl && (
                                                    <img
                                                        src={botDetails.botImageUrl}
                                                        alt="Bot"
                                                        style={{ width: '24px', height: '24px', borderRadius: '50%', marginRight: '8px', flexShrink: 0 }}
                                                        loading="lazy"
                                                    />
                                                )}
                                                <BubbleContent position={msg.position}>
                                                    {msg.position === 'left' ? <RichMessage markdown={msg.text} /> : msg.text}
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                                                        {new Date(msg.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                    </Typography>
                                                </BubbleContent>
                                            </MessageBubble>
                                        ))}
                                        <AnimatePresence>
                                            {isTyping && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 10 }}
                                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pl: 1 }}>
                                                        <img
                                                            src={botDetails?.botImageUrl || 'https://i.imgur.com/2OPgGx8.gif'}
                                                            alt="Bot"
                                                            style={{ width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0 }}
                                                            loading="lazy"
                                                        />
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <motion.div
                                                                animate={{ y: [0, -4, 0] }}
                                                                transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut', delay: 0 }}
                                                                sx={{ width: '10px', height: '10px', bgcolor: 'primary.main', borderRadius: '50%', boxShadow: '0 0 4px rgba(168, 85, 247, 0.5)' }}
                                                            />
                                                            <motion.div
                                                                animate={{ y: [0, -4, 0] }}
                                                                transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
                                                                sx={{ width: '10px', height: '10px', bgcolor: 'primary.main', borderRadius: '50%', boxShadow: '0 0 4px rgba(168, 85, 247, 0.5)' }}
                                                            />
                                                            <motion.div
                                                                animate={{ y: [0, -4, 0] }}
                                                                transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
                                                                sx={{ width: '10px', height: '10px', bgcolor: 'primary.main', borderRadius: '50%', boxShadow: '0 0 4px rgba(168, 85, 247, 0.5)' }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ ml: 1, color: 'text.primary', fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontStyle: 'italic' }}
                                                                aria-label="Bot is typing"
                                                            >
                                                                Typing...
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                        <div ref={messagesEndRef} />
                                    </>
                                )}
                            </MessageArea>
                            <InputSection>
                                <ErrorBoundary>
                                    <SuggestedQuestions
                                        questions={suggestedQuestions}
                                        onQuestionClick={handleQuestionClick}
                                        error={questionError}
                                    />
                                </ErrorBoundary>
                                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mt: 1 }}>
                                    <CustomTextField
                                        inputRef={inputRef}
                                        fullWidth
                                        value={userInput}
                                        onChange={e => setUserInput(e.target.value)}
                                        onKeyDown={handleSendKeyDown}
                                        disabled={!botDetails?.botId || messageCount >= 15 || isSending || isLoading}
                                        placeholder={inputPlaceholder}
                                        variant="outlined"
                                        size="small"
                                        tabIndex={0}
                                        aria-label="Type your message (15 messages max)"
                                    />
                                    <AnimatedTooltip title="Send message">
                                        <CustomIconButton
                                            onClick={handleSend}
                                            onKeyDown={handleSendKeyDown}
                                            disabled={!botDetails?.botId || messageCount >= 15 || isSending || !userInput.trim() || isLoading}
                                            color="primary"
                                            tabIndex={0}
                                            aria-label="Send message"
                                            aria-disabled={!botDetails?.botId || messageCount >= 15 || isSending || !userInput.trim() || isLoading}
                                        >
                                            {isSending ? <CircularProgress size={24} /> : <SendIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />}
                                        </CustomIconButton>
                                    </AnimatedTooltip>
                                </Box>
                            </InputSection>
                            <Footer>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <a
                                        href="https://vuxo.aiempower.ca"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        tabIndex={0}
                                        aria-label="Visit AI Empower website"
                                        onKeyDown={handleLinkKeyDown}
                                    >
                                        <img src="https://i.imgur.com/0bKyom7.png" alt="AI Empower" style={{ height: '20px' }} loading="lazy" />
                                    </a>
                                    <ShinyLink
                                        href="https://vuxo.aiempower.ca"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        isFooter
                                        tabIndex={0}
                                        aria-label="Powered By AI Empower"
                                        onKeyDown={handleLinkKeyDown}
                                    >
                                        Powered By AI Empower
                                    </ShinyLink>
                                </Box>
                                {messageCount < 15 ? (
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: theme => alpha(theme.custom.textColor, 0.6) }}>
                                        Messages left: {15 - messageCount}/15
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: 'error.main' }}>
                                        Message limit reached.
                                    </Typography>
                                )}
                            </Footer>
                            {!isAlways && !isFullScreen && (
                                <AnimatedTooltip title="Resize chat">
                                    <ResizeHandle
                                        ref={resizeRef}
                                        onMouseDown={handleResizeStart}
                                        onTouchStart={handleResizeStart}
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Resize chat"
                                    />
                                </AnimatedTooltip>
                            )}
                        </ChatPanel>
                    </motion.div>
                )}
            </AnimatePresence>
        </ThemeProvider>
    );
};

export default EmbeddableChat;